import { navigate } from "gatsby";
import _ from "lodash";
import getApiData from "./getApiData";
import Encryption from "../helpers/encryption";

interface SendEventToGAProps {
  type: string;
  parameters: object;
}

export const sendEventToGA = (props: SendEventToGAProps) => {
  const { type, parameters } = props;

  if (process.env.NODE_ENV === "development") {
    console.log(`Submit click event to GA`);
    console.log("🚀 ~ sendEventToGA ~ type:", type);
    console.log("🚀 ~ sendEventToGA ~ parameters:", parameters);
  }

  if (process.env.NODE_ENV === "production") {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    typeof window !== "undefined" && window.gtag("event", type, parameters);
  }
};

export const parseLocation = (props: any) => {
  if (props && props.location && props.location.search) {
    return returnPairs({ paramsArray: props.location.search });
  }
  return "";
};

interface returnPairsProps {
  paramsArray: any;
}

export const returnPairs = (props: returnPairsProps) => {
  return _.fromPairs(
    // props.location.search
    props.paramsArray
      .replace("?", "")
      .split("&")
      .map(_.partial(_.split, _, "=", 2))
  );
};

export const validateToken = async (token: string, redirect = true) => {
  try {
    if (token) {
      const userHasValidToken = await getApiData({
        endpoint: "security/validateToken",
        method: "post",
        headers: {
          "X-Amz-Security-Token": token,
        },
      });

      if (redirect) {
        if (userHasValidToken?.status !== 200) {
          // Redirect the user to the login page when token is not valid
          // Navigate user to logout page
          navigate("/logout");
        } else {
          return true;
        }
      } else {
        return userHasValidToken?.status === 200;
      }
    }
  } catch (error) {
    console.log("🚀 ~ validateToken ~ error:", error);
    return false;
  }
  return false;
};

/**
 * Flatten an Dictionary Data array
 * @param data
 * @returns flat array
 */
export const flattenDD = (data: type) => {
  let newArray: any[] = [];
  if (data.length > 0) {
    data.map((item: any) => {
      const newItem = { ...item };
      delete newItem.type;
      newArray.push(newItem);
      if (item.type) {
        const subItems = flattenDD(item.type);
        newArray = concat(newArray, subItems);
      }
    });
  }
  return newArray;
};

/**
 * Get user rights object
 */

export const userRightsDecoded = (
  objectId: string,
  userRightsString: string
) => {
  const password = `${objectId}-dti-2023`;
  return Encryption.decryptObject(userRightsString, password);
};

/**
 * Return the correct `project name` so different components and styles can be used
 */
export const getProjectName = (ReturnCapitals = false) => {
  let projectName = "iris";

  try {
    if (typeof window !== "undefined") {
      // Client-side-only code
      const domainName = window.location.hostname;

      if (domainName.includes("iris")) {
        projectName = "iris";
      }

      if (domainName.includes("oasis")) {
        projectName = "oasis";
      }
    }
  } catch (error) {
    console.log("🚀 ~ getProjectName ~ error:", error);
  }

  // To set the `projectName` manually uncommand these:

  if (process.env.GATSBY_SITE === "OASIS") {
    projectName = "oasis";
  }

  if (process.env.GATSBY_SITE === "IRIS") {
    projectName = "iris";
  }

  // projectName = "oasis";
  // projectName = "iris";

  return ReturnCapitals ? projectName.toUpperCase() : projectName;
};

export const extractS3Key = (uploadURL: string): string => {
  // Match the part of the URL after the bucket name
  const match = uploadURL.match(/https:\/\/[^/]+\/(.+)/);
  // Return the matched group or an empty string if no match
  return match ? match[1] : "";
};

/**
 * Simple function to turn a string into a valid CSS class name
 * @param str
 * @returns str
 */
export function stringToClassName(str: string) {
  return str
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .toLowerCase();
}

/**
 * Function to check if DD type is New
 * @param obj {}
 * @returns boolean
 */
export function isNew(obj: any) {
  if (obj && obj.type && obj.type.toLowerCase() === "new") {
    return true;
  }
  return false;
}

export function formatDate(dateString: string) {
  // Parse the date string into a Date object
  const date = new Date(dateString);

  // Get day, month, and year
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const year = date.getFullYear();

  // Format and return the date as dd-mm-yyyy
  return `${day}-${month}-${year}`;
}

export const ensureEndsWithPunctuation = (str: string) => {
  if (!str) return ""; // Handle undefined or null
  const punctuation = [".", "?", "!"];
  return punctuation.includes(str.slice(-1)) ? str : `${str}.`;
};

/**
 * Function to return a creator as [lastName], [firtNameInitials]
 * @param firstName string
 * @param lastName string
 * @returns string
 */
export const formatName = (firstName: string, lastName: string): string => {
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (!lastName) {
    const parts = firstName.trim().split(/\s+/);
    if (parts.length === 0) return "";

    const last = parts[parts.length - 1];
    const formattedLast = last.endsWith(".")
      ? capitalize(last)
      : `${capitalize(last[0])}.`;

    const formattedFirstName = firstName
      .split(/\s+/)
      .map((part) => {
        if (part.endsWith(".")) {
          return capitalize(part);
        }
        return `${capitalize(part[0])}.`;
      })
      .join(" ");

    return `${formattedLast}, ${formattedFirstName}`;
  }

  const formattedFirstName = firstName
    .split(/\s+/)
    .map((part) =>
      part
        .split("-")
        .map((subPart) => {
          if (!subPart) return "";
          const trimmed = subPart.trim();
          if (trimmed.endsWith(".")) {
            return capitalize(trimmed);
          }
          return `${capitalize(trimmed[0])}.`;
        })
        .join("-")
    )
    .join(" ");

  return ensureEndsWithPunctuation(`${lastName}, ${formattedFirstName}`);
};

/**
 * Ensures the name is formatted as 'Lastname, I.'
 * @param name string in the format 'Lastname, Firstname'
 * @returns string
 */
export const formatSingleName = (name: string): string => {
  const parts = name.split(",").map((subPart) => subPart.trim());

  if (parts.length < 2) {
    return name;
  }

  const lastName = parts[0];
  const firstName = parts.slice(1).join(" ");

  const isAlreadyInitials = /^[A-Z](\.|-)?([A-Z](\.|-)?)*\.?$/.test(
    firstName.replace(/\s+/g, "")
  );

  if (isAlreadyInitials) {
    return ensureEndsWithPunctuation(`${lastName}, ${firstName}`);
  }

  return formatName(firstName, lastName);
};

export const splitSingleName = (name: string): string[] => {
  const [lastName, firstName = ""] = name
    .split(", ")
    .map((part) => part.trim());

  return [firstName, lastName];
};

/**
 * Filter the `options` to remove `REJECTED` and `NEW` options unless it's specific for this material.
 */
export const filterOptions = (options: any, objectId: string) => {
  if (!options || options.length === 0) return [];
  const filteredOptions = options
    .filter((item: any) => {
      const isRejectedOrNew = item.status === "REJECTED" || item.status === "NEW";
      const isLinkedToMaterial = Array.isArray(item.materialId) && item.materialId.includes(objectId);
      return !isRejectedOrNew || isLinkedToMaterial;
    })
    .filter((item: any) => {
      try {
        if (item.label !== undefined) {
          const lc = item.label.toString().toLowerCase();
          if (lc && lc === "other") {
            return null;
          }
        }
      } catch (error) {
        console.log("🚀 ~ .filter ~ error:", error);
        console.log("🚀 ~ .filter ~ item.label:", item.label);
      }

      return item.label;
    });

  return filteredOptions;
}